import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Users, Building2, Target, Eye } from "lucide-react";
import { Button } from "../components/ui/button";
import { aboutImage as fallbackAboutImage, milestones as fallbackMilestones, whyUs as fallbackWhyUs, capabilities as fallbackCapabilities } from "../data";
import { getSiteContent } from "../cms/siteQueries";

export default function About() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let mounted = true;
    getSiteContent()
      .then((data) => { if (mounted && data) setContent(data); })
      .catch((error) => console.error("Unable to load About content from Sanity:", error));
    return () => { mounted = false; };
  }, []);

  const about = content?.about || {};
  const aboutImage = content?.home?.aboutImage || fallbackAboutImage;
  const milestones = content?.milestones || fallbackMilestones;
  const whyUs = content?.whyUs || fallbackWhyUs;
  const capabilities = content?.capabilities || fallbackCapabilities;

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative bg-[#0a1929] text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-25 bg-cover bg-center" style={{ backgroundImage: `url(${capabilities[0].image})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1929] via-[#0a1929]/90 to-[#0a1929]/30" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-[#ff6b7a] font-bold text-xs uppercase tracking-[0.3em] mb-4">About Us</div>
          <h1 className="font-black text-5xl lg:text-6xl leading-tight tracking-tight mb-5">Who We Are</h1>
          <p className="text-slate-300 max-w-2xl text-lg">
            {about.heroDescription || "HIG Automation India Private Limited — a Delhi NCR-based engineering services company built to modernise Indian manufacturing."}
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={aboutImage} alt="HIGA team" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-[#c8102e] font-bold text-xs uppercase tracking-[0.3em] mb-4">Our Story</div>
            <h2 className="font-black text-4xl text-[#0a1929] tracking-tight leading-tight mb-6">{about.storyHeading || "From Concept to Commissioning — All In One Place."}</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {about.storyParagraph1 || "Incorporated in June 2025, HIGA has quickly grown into a full-stack industrial engineering partner. We specialize in factory automation, pneumatic systems, conveyor systems, fire protection, AHU installations, and precision fabrication — delivering complete turnkey solutions."}
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              {about.storyParagraph2 || "We combine deep technical expertise with hands-on execution — our engineers have 12+ years of combined field experience across automotive and heavy manufacturing."}
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="bg-slate-50 p-6 border-l-4 border-[#c8102e]">
                <Target className="w-7 h-7 text-[#c8102e] mb-3" strokeWidth={1.5} />
                <h3 className="font-black text-[#0a1929] text-lg mb-2">Mission</h3>
                <p className="text-sm text-slate-600">{about.mission || "To become India’s preferred end-to-end industrial automation partner, known for speed, precision, and after-sales reliability."}</p>
              </div>
              <div className="bg-slate-50 p-6 border-l-4 border-[#0a1929]">
                <Eye className="w-7 h-7 text-[#0a1929] mb-3" strokeWidth={1.5} />
                <h3 className="font-black text-[#0a1929] text-lg mb-2">Vision</h3>
                <p className="text-sm text-slate-600">{about.vision || "Empower manufacturers with smart, cost-effective automation that improves productivity and reduces downtime — every time."}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-[#c8102e] font-bold text-xs uppercase tracking-[0.3em] mb-4">Growth Story</div>
            <h2 className="font-black text-4xl lg:text-5xl text-[#0a1929] tracking-tight leading-tight mb-5">Key Milestones</h2>
            <p className="text-slate-600 italic">{about.growthStoryText || "Igniting Growth: Transforming Vision into Scale Reality."}</p>
          </div>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-[#c8102e]/30 md:-translate-x-1/2" />
            <div className="space-y-10">
              {milestones.map((m, i) => (
                <div key={i} className={`relative grid md:grid-cols-2 gap-8 items-center`}>
                  <div className={`${i % 2 === 0 ? "md:text-right md:pr-12" : "md:col-start-2 md:pl-12"} pl-12 md:pl-0`}>
                    <div className="text-[#c8102e] font-black text-xl mb-1">{m.date}</div>
                    <div className="font-black text-[#0a1929] text-2xl mb-2">{m.title}</div>
                    <p className="text-slate-600">{m.desc}</p>
                  </div>
                  <div className="absolute left-4 md:left-1/2 w-4 h-4 rounded-full bg-[#c8102e] border-4 border-white shadow md:-translate-x-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Manufacturing Footprint */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-[#c8102e] font-bold text-xs uppercase tracking-[0.3em] mb-4">Manufacturing Footprint</div>
            <h2 className="font-black text-4xl lg:text-5xl text-[#0a1929] tracking-tight leading-tight">Where We Operate</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#0a1929] text-white p-8">
              <MapPin className="w-8 h-8 text-[#c8102e] mb-4" strokeWidth={1.5} />
              <h3 className="font-black text-xl mb-3">{about.locationTitle || "Kaluwas, Rewari"}</h3>
              <p className="text-slate-300 text-sm mb-4">{about.locationDescription || "Plot No. 121, Kaluwas Road, Rewari, Haryana — 123401"}</p>
              <div className="text-xs uppercase tracking-widest text-slate-400">{about.locationNote || "60 km from Delhi Airport • NH-48 Access"}</div>
            </div>
            <div className="bg-slate-50 p-8 border border-slate-200">
              <Building2 className="w-8 h-8 text-[#c8102e] mb-4" strokeWidth={1.5} />
              <h3 className="font-black text-[#0a1929] text-xl mb-3">Plant Area</h3>
              <p className="text-slate-600 text-sm mb-1">Phase 1: <strong>{about.plantPhase1 || "450 sqm (Operational)"}</strong></p>
              <p className="text-slate-600 text-sm">New Factory: <strong>{about.plantExpansion || "800 sqm + Warehouse + Offices (2026)"}</strong></p>
            </div>
            <div className="bg-slate-50 p-8 border border-slate-200">
              <Users className="w-8 h-8 text-[#c8102e] mb-4" strokeWidth={1.5} />
              <h3 className="font-black text-[#0a1929] text-xl mb-3">Team &amp; Clients</h3>
              <div className="flex gap-8">
                <div><div className="text-3xl font-black text-[#0a1929]">{about.engineersCount || "22"}</div><div className="text-xs uppercase tracking-widest text-slate-500">Engineers</div></div>
                <div><div className="text-3xl font-black text-[#0a1929]">{about.projectsCount || "150+"}</div><div className="text-xs uppercase tracking-widest text-slate-500">Projects</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why HIGA */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-[#c8102e] font-bold text-xs uppercase tracking-[0.3em] mb-4">Why HIGA</div>
            <h2 className="font-black text-4xl lg:text-5xl text-[#0a1929] tracking-tight leading-tight">One Team. One Goal.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyUs.map((w) => (
              <div key={w.title} className="bg-white p-8 border border-slate-200 hover:border-[#c8102e] transition-colors">
                <h3 className="font-black text-[#0a1929] text-lg mb-3 tracking-tight">{w.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{w.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#c8102e] text-white py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h3 className="font-black text-3xl lg:text-4xl mb-4">{about.ctaHeading || "Let’s Build Something Great Together."}</h3>
          <Link to="/contact">
            <Button className="bg-white text-[#c8102e] hover:bg-slate-100 rounded-none px-10 py-6 text-sm font-bold uppercase tracking-wide gap-2">
              Start The Conversation <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
